import { useContent } from "@/content/ContentProvider";
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Pill } from "./Reveal";
import { PillButton, ImageReveal } from "./Kit";
import ShaderBackground from "./ShaderBackground";
import WorkflowDiagram from "./WorkflowDiagram";
export function AudienceCard({ audience: a }) {
  const { text: cmsText } = useContent();
  return (
    <Link
      to={`/for/${a.slug}`}
      data-testid={`audience-${a.slug}`}
      data-cursor="Explore"
      className="group block"
    >
      <ImageReveal className="relative aspect-[4/3] min-h-[320px] w-full min-w-0 overflow-hidden rounded-[24px] bg-[#efefed] sm:min-h-[360px]">
        <WorkflowDiagram steps={a.steps} />
      </ImageReveal>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[12px] opacity-50">{a.name}</p>
          <h3 className="mt-1 text-[24px] leading-[1.08] tracking-[-0.025em] sm:text-[30px]">
            {a.title}
          </h3>
          <p className="mt-3 max-w-[540px] text-[15px] leading-[1.4] opacity-60">
            {a.line}
          </p>
          <div className="mt-3 flex flex-wrap gap-1">
            {a.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-[#f7f7f7] px-3 py-1 text-[11px]"
              >
                {t}
              </span>
            ))}
          </div>
          <span className="nm-link mt-4 inline-block text-[12px] font-medium">
            {cmsText("HomeBottom.AudienceCard.explore-workflows.4933f1")}
          </span>
        </div>
        <span className="mt-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f7f7f7] transition-colors duration-300 group-hover:bg-black group-hover:text-white">
          <ArrowUpRight
            size={13}
            className="transition-transform duration-500 group-hover:rotate-45"
          />
        </span>
      </div>
    </Link>
  );
}
export function AudiencesSection() {
  const { text: cmsText, AUDIENCES } = useContent();
  return (
    <section
      id="audiences"
      className="scroll-mt-24 px-5 pt-28 lg:pt-44"
      data-testid="audiences-section"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Pill>
              {cmsText("HomeBottom.AudiencesSection.who-it-s-for.380adc")}
            </Pill>
            <Reveal className="mt-5">
              <h2 className="nm-title max-w-[760px] text-[34px] leading-[1.03] sm:text-[56px]">
                {cmsText(
                  "HomeBottom.AudiencesSection.built-around-your-work-.8c4068",
                )}
              </h2>
              <p className="mt-5 max-w-[620px] text-[18px] leading-[1.3] opacity-60">
                {cmsText(
                  "HomeBottom.AudiencesSection.two-places-a-connected-workflow-could-help-th.67c057",
                )}
              </p>
            </Reveal>
          </div>
          <PillButton to="/for" variant="light">
            {cmsText("HomeBottom.AudiencesSection.find-your-workflow.48c6f9")}
          </PillButton>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-x-3 gap-y-14 md:grid-cols-2">
          {AUDIENCES.filter((a) => a.visibility !== "unlisted").map((a, i) => (
            <Reveal key={a.slug} delay={i * 100}>
              <AudienceCard audience={a} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
export function PracticeSection() {
  const { text: cmsText } = useContent();
  return (
    <section
      id="about"
      className="scroll-mt-24 px-5 pt-28 lg:pt-44"
      data-testid="practice-section"
    >
      <div className="mx-auto max-w-[1400px]">
        <Pill>{cmsText("HomeBottom.PracticeSection.how-we-work.fc03ef")}</Pill>
        <div className="mt-3 grid gap-2 lg:grid-cols-12">
          <Reveal className="flex min-h-[400px] flex-col justify-between rounded-[28px] bg-[#f7f7f7] p-6 sm:p-10 lg:col-span-8">
            <div>
              <p className="nm-title max-w-[840px] text-[28px] leading-[1.12] sm:text-[42px]">
                {cmsText(
                  "HomeBottom.PracticeSection.we-ve-built-internal-applications-that-work-t.f17577",
                )}
              </p>
              <p className="mt-6 max-w-[650px] text-[17px] leading-[1.4] opacity-60">
                {cmsText(
                  "HomeBottom.PracticeSection.we-design-the-workflow-build-the-connections-.cf96c7",
                )}
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
              <span className="text-[13px] opacity-50">
                {cmsText(
                  "HomeBottom.PracticeSection.one-point-of-contact-a-clearly-scoped-engagem.19838a",
                )}
              </span>
              <PillButton to="/about" variant="white">
                {cmsText("HomeBottom.PracticeSection.about-nomus.0cc569")}
              </PillButton>
            </div>
          </Reveal>
          <Reveal className="min-h-[360px] overflow-hidden rounded-[28px] lg:col-span-4">
            <WorkflowDiagram />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
export function PaperCard({ p }) {
  const { text: cmsText, IMG } = useContent();
  return (
    <Link
      to={`/insights/${p.id}`}
      data-testid={`paper-${p.id}`}
      data-cursor="Read"
      className="group block"
    >
      <ImageReveal className="nm-img-zoom overflow-hidden rounded-[20px]">
        <img
          src={IMG[p.img] || p.img || "/images/nomus-handoff-v1.webp"}
          alt={p.imageAlt || p.title}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover grayscale"
        />
      </ImageReveal>
      <div className="mt-4 flex items-center justify-between text-[12px] opacity-40">
        <span>
          {p.cat}
          {cmsText("HomeBottom.PaperCard.-note.68f17e")}
        </span>
        <span>
          {p.read}
          {cmsText("HomeBottom.PaperCard.min.f4e8a8")}
        </span>
      </div>
      <h3 className="mt-1 text-[22px] leading-[1.1] tracking-[-0.025em]">
        <span className="nm-link">{p.title}</span>
      </h3>
      <p className="mt-2 line-clamp-3 text-[14px] leading-[1.35] opacity-60">
        {p.text}
      </p>
    </Link>
  );
}
export function InsightsSection() {
  const { text: cmsText, PAPERS } = useContent();
  return (
    <section className="px-5 pt-28 lg:pt-44" data-testid="insights-section">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Pill>
              {cmsText(
                "HomeBottom.InsightsSection.notes-on-connected-work.1dc2ab",
              )}
            </Pill>
            <Reveal className="mt-5">
              <h2 className="nm-title text-[34px] leading-[1.03] sm:text-[56px]">
                {cmsText(
                  "HomeBottom.InsightsSection.how-we-approach-the-build-.141c95",
                )}
              </h2>
            </Reveal>
          </div>
          <PillButton to="/insights" variant="light">
            {cmsText("HomeBottom.InsightsSection.all-notes.a24d47")}
          </PillButton>
        </div>
        <div className="mt-12 grid gap-x-3 gap-y-10 md:grid-cols-3">
          {PAPERS.filter((p) => p.visibility !== "unlisted")
            .slice(0, 3)
            .map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <PaperCard p={p} />
              </Reveal>
            ))}
        </div>
      </div>
    </section>
  );
}
export function ContactBlock() {
  const { text: cmsText } = useContent();
  return (
    <section
      id="contact"
      className="scroll-mt-24 px-3 pt-28 sm:px-5 lg:pt-44"
      data-testid="contact-block"
    >
      <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[28px] bg-black text-white">
        <ShaderBackground dark strength={0.42} lines={14} fadeLeft={0.2} />
        <div className="relative z-10 grid items-end gap-8 p-6 sm:p-12 lg:grid-cols-12 lg:p-16">
          <Reveal className="overflow-hidden rounded-[20px] text-black lg:col-span-4">
            <WorkflowDiagram
              steps={[
                "The job your team puts off",
                "A tool built around it",
                "A result you can check",
              ]}
            />
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6">
            <Pill white>
              {cmsText(
                "HomeBottom.ContactBlock.start-with-one-workflow.8250d2",
              )}
            </Pill>
            <Reveal className="mt-6">
              <h2 className="nm-title text-[38px] leading-[1.0] sm:text-[64px]">
                {cmsText(
                  "HomeBottom.ContactBlock.what-keeps-getting-stuck-.f9706c",
                )}
              </h2>
              <p className="mt-6 max-w-[520px] text-[20px] leading-[1.25] tracking-[-0.02em] text-white/80 sm:text-[24px]">
                {cmsText(
                  "HomeBottom.ContactBlock.tell-us-what-the-job-involves-which-tools-you.a29b34",
                )}
              </p>
              <div className="mt-8">
                <PillButton
                  to="/contact"
                  variant="inverse"
                  size="lg"
                  data-testid="contact-block-cta"
                >
                  {cmsText(
                    "HomeBottom.ContactBlock.discuss-your-workflow.184fa5",
                  )}
                </PillButton>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
